import { apiFetch } from "./client";

export type InsightsPeriod = "7d" | "30d" | "90d";
export const INSIGHTS_PERIODS: InsightsPeriod[] = ["7d", "30d", "90d"];

export type FailureKind =
  | "tool_timeout"
  | "tool_error"
  | "await_timeout"
  | "agent_error"
  | "validation"
  | "cancelled"
  | "other";

export interface InsightsTotals {
  runs: number;
  completed: number;
  failed: number;
  cancelled: number;
  live: number;
  durationMsTotal: number;
  avgDurationMs: number | null;
  successRate: number | null;
  tokensIn: number;
  tokensOut: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  toolCalls: number;
  costCents: number;
  priced: boolean;
  turns: number;
  priorRuns: number;
  runsChangePct: number | null;
  costPerRunCents: number | null;
}

export interface TriggerSummary {
  subtype: string;
  label: string | null;
  cronExpression: string | null;
  timezone: string | null;
  channelId: string | null;
  channelName: string | null;
}

export interface FailureReason {
  kind: FailureKind;
  label: string;
  count: number;
}

export interface DayPoint {
  date: string;
  completed: number;
  failed: number;
}

export interface WorkflowInsightRow {
  id: string;
  name: string;
  status: string;
  trigger: TriggerSummary;
  agentIds: string[];
  runs: number;
  completed: number;
  failed: number;
  successRate: number | null;
  avgDurationMs: number | null;
  tokensIn: number;
  tokensOut: number;
  costCents: number;
  priced: boolean;
  lastRunAt: string | null;
}

export interface CompanyInsights {
  period: InsightsPeriod;
  days: number;
  since: string;
  until: string;
  agentId: string | null;
  totals: InsightsTotals;
  failureReasons: FailureReason[];
  perDay: DayPoint[];
  workflows: WorkflowInsightRow[];
}

export interface ChainNode {
  id: string;
  type: "trigger" | "agent" | "condition" | "action" | "decision";
  data: Record<string, unknown>;
}

export interface WorkflowInsights {
  workflow: {
    id: string;
    name: string;
    status: string;
    trigger: TriggerSummary;
    agentIds: string[];
    chain: ChainNode[];
  };
  period: InsightsPeriod;
  days: number;
  since: string;
  until: string;
  totals: InsightsTotals;
  failureReasons: FailureReason[];
  perDay: DayPoint[];
}

export interface InsightRunRow {
  id: string;
  status: string;
  triggerSource: string | null;
  currentStepId: string | null;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  tokensIn: number;
  tokensOut: number;
  toolCalls: number;
  failureKind: FailureKind | null;
  errorMessage: string | null;
  sessionId: string | null;
}

export interface InsightRunsPage {
  runs: InsightRunRow[];
  hasMore: boolean;
  failedCount: number;
}

export interface InsightStep {
  stepId: string;
  nodeType: "trigger" | "agent" | "condition" | "action" | string;
  label: string;
  agentId: string | null;
  status: "completed" | "failed" | "running" | "awaiting" | "skipped" | "pending";
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  attempt: number;
  branchTaken: string | null;
  tokensIn: number;
  tokensOut: number;
  toolCalls: number;
  error: { message: string; detail: string | null } | null;
  payloadPreview: string | null;
  outputPreview: string | null;
}

export interface RunInsights {
  run: {
    id: string;
    workflowId: string;
    status: string;
    triggerSource: string | null;
    startedAt: string;
    completedAt: string | null;
    durationMs: number | null;
    currentStepId: string | null;
    failedStepIndex: number | null;
    failureKind: FailureKind | null;
    errorMessage: string | null;
    sessionId: string | null;
    assignedAgent: { id: string; name: string } | null;
  };
  workflow: { id: string; name: string } | null;
  steps: InsightStep[];
  totals: {
    tokensIn: number;
    tokensOut: number;
    toolCalls: number;
    turns: number;
    costCents: number;
    priced: boolean;
  };
  similarFailures: number;
}

export const workflowInsightsApi = {
  company: (companyId: string, period: InsightsPeriod, agentId?: string | null) => {
    const qs = new URLSearchParams({ period });
    if (agentId) qs.set("agentId", agentId);
    return apiFetch<CompanyInsights>(`/companies/${companyId}/workflows/insights?${qs}`);
  },
  workflow: (workflowId: string, period: InsightsPeriod) =>
    apiFetch<WorkflowInsights>(`/workflows/${workflowId}/insights?period=${period}`),
  runs: (
    workflowId: string,
    opts: { period: InsightsPeriod; status?: "failed" | "completed" | null; limit?: number; offset?: number },
  ) => {
    const qs = new URLSearchParams({ period: opts.period, limit: String(opts.limit ?? 50) });
    if (opts.status) qs.set("status", opts.status);
    if (opts.offset) qs.set("offset", String(opts.offset));
    return apiFetch<InsightRunsPage>(`/workflows/${workflowId}/insights/runs?${qs}`);
  },
  run: (runId: string) => apiFetch<RunInsights>(`/workflow-runs/${runId}/insights`),
};
